/*
 * Playground: conversar con la IA sobre una zona caliente, dentro de la cuenta.
 *
 * Se ve como un chat. A la izquierda, dos vistas: las conversaciones (el historial)
 * y el contexto que lee la IA (zona, perfil de la empresa, cómo se hace hoy, ideas
 * anteriores, la taxonomía, y uno por uno los hallazgos, las transcripciones y las
 * interacciones, más las instrucciones de siempre, editables). A la derecha, la
 * conversación: la caja ya trae el primer mensaje de siempre; las respuestas traen
 * texto y, cuando se piden ideas, tarjetas. Cada tarjeta se guarda (★), se descarta
 * con motivo, se desglosa o se varía (eso manda un mensaje), o se vuelve ficha.
 *
 * Lo que no depende del prompt se aplica igual aunque se editen las instrucciones:
 * solo cuentan anclas verificadas y la ficha nace por `crear_oportunidad`.
 */

import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { ErrorApi, idNuevo } from "../../api/cliente";
import {
  useCandidatos, useContextoPlayground, useConversacion, useConversaciones, useConvertirIdea, useMandarMensaje, useMarcarIdea, usePersonas,
  useRenombrarConversacion, useVerificados, useVistaPrevia, useZonas,
} from "../../api/hooks";
import {
  CONTEXTOS_RONDA, ESFUERZOS, SESIONES, TIPOS_HALLAZGO, TIPOS_INTERACCION, nombreEquipo, type CasoResumen, type ContextoConversacion, type Esfuerzo,
  type ContextoRonda, type CuentaDetalle, type Hallazgo, type IdeaExplorada, type MensajeExploracion, type VistaPreviaPrompt, type Zona,
} from "../../api/tipos";
import { useFormularios } from "../../shell/Formularios";
import { Modal } from "../../ui/Modal";
import { Abierto, Esqueleto, Pastilla, fmtFecha } from "../../ui/primitivas";
import { FormOportunidad } from "./Embudos";

const TOPE_TRANSCRIPCION = 400_000; // el mismo que en el servidor
const LLAVE_INSTRUCCIONES = "nexlum.playground.instrucciones";

const SUGERENCIAS = [
  "Propón 3 ideas más, distintas a las anteriores.",
  "Dame versiones más simples de las que guardé: algo que se pruebe en dos semanas.",
  "Propón ideas más ambiciosas, que resuelvan esto de raíz.",
  "Compara las ideas guardadas: ¿cuál harías primero y por qué?",
  "¿Qué datos nos faltan para medir el antes de estas ideas?",
];

function mensaje(e: unknown): string {
  return e instanceof ErrorApi ? e.message : "No se pudo completar. Revisa la conexión e inténtalo otra vez.";
}

// Las instrucciones editadas se recuerdan en este navegador: es el prompt con el que
// cada quien está experimentando. Si el almacenamiento falla, simplemente no se recuerda.
function leerInstrucciones(): string | null {
  try { return localStorage.getItem(LLAVE_INSTRUCCIONES); } catch { return null; }
}
function guardarInstrucciones(v: string | null): void {
  try { if (v === null) localStorage.removeItem(LLAVE_INSTRUCCIONES); else localStorage.setItem(LLAVE_INSTRUCCIONES, v); } catch { /* sin almacenamiento */ }
}

export function Playground({ c, caso }: { c: CuentaDetalle; caso: CasoResumen | null }) {
  const { abrir } = useFormularios();
  if (!caso) {
    return (
      <Abierto style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 240 }}>Las conversaciones se guardan en el expediente del descubrimiento. Inícialo (basta con elegir quién facilita) y aquí mismo podrás conversar con lo que ya tienes de la cuenta.</span>
        <button className="btn btn-primario" onClick={() => abrir({ tipo: "descubrimiento", cuentaId: c.id, empresa: c.empresa })}>Iniciar descubrimiento →</button>
      </Abierto>
    );
  }
  return <Chat cuentaId={c.id} casoId={caso.id} empresa={c.empresa} />;
}

const SIN_ZONA = "ninguna";

function Chat({ cuentaId, casoId, empresa }: { cuentaId: string; casoId: string; empresa: string }) {
  const [query, setQuery] = useSearchParams();
  const { data: zonas } = useZonas(casoId);
  const { data: verificados } = useVerificados(casoId);
  const { data: candidatos } = useCandidatos(casoId);
  const { data: personas } = usePersonas(casoId);
  const { data: info } = useContextoPlayground(casoId);
  const { data: lista } = useConversaciones(casoId);

  const convId = query.get("conv");
  const { data: conv } = useConversacion(casoId, convId);
  const activas = useMemo(() => (zonas ?? []).filter((z) => z.estado !== "descartada"), [zonas]);
  // Sin zonas calientes se conversa sobre la cuenta en general; con zonas, la primera por defecto.
  const pedida = query.get("zona");
  const zonaNueva = pedida === SIN_ZONA ? null : pedida && activas.some((z) => z.id === pedida) ? pedida : activas[0]?.id ?? null;
  const zonaId: string | null = conv ? conv.zona_caliente_id : convId ? null : zonaNueva;
  const zona = (zonas ?? []).find((z) => z.id === zonaId);
  const nombreZona = useMemo(() => new Map((zonas ?? []).map((z) => [z.id, z.nombre])), [zonas]);

  // ── Contexto ──
  const deLaZona = useMemo(
    () => (verificados ?? []).filter((h) => !zonaId || h.zona_caliente_id === zonaId || zona?.hallazgos_evidencia.includes(h.id)),
    [verificados, zonaId, zona],
  );
  const candidatosZona = useMemo(() => (candidatos ?? []).filter((h) => !zonaId || h.zona_caliente_id === zonaId), [candidatos, zonaId]);
  const sesionesConTexto = useMemo(() => info?.sesiones ?? [], [info]);
  const interaccionesCuenta = useMemo(() => info?.interacciones ?? [], [info]);
  const [contextos, setContextos] = useState<ContextoRonda[]>(CONTEXTOS_RONDA.map((x) => x.id));
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [candSel, setCandSel] = useState<string[]>([]);
  const [sesiones, setSesiones] = useState<string[]>([]);
  const [interacciones, setInteracciones] = useState<string[]>([]);
  const [instrucciones, setInstrucciones] = useState<string | null>(() => leerInstrucciones());
  const [esfuerzo, setEsfuerzo] = useState<Esfuerzo>("medium");

  // Al abrir una conversación se recupera el contexto con que se mandó su último
  // mensaje. En una nueva va todo lo que hay, mientras quepa: los verificados (de la
  // zona, si hay zona), los sin verificar, las interacciones y las transcripciones.
  const ultimoCtx = conv ? [...conv.mensajes].reverse().find((m) => m.contexto)?.contexto ?? null : null;
  useEffect(() => {
    if (ultimoCtx) {
      setElegidos(ultimoCtx.hallazgos_incluidos);
      setCandSel(ultimoCtx.candidatos_incluidos ?? []);
      setSesiones(ultimoCtx.sesiones_incluidas);
      setInteracciones(ultimoCtx.interacciones_incluidas);
      setContextos(ultimoCtx.contextos);
    } else if (!convId) {
      setElegidos(deLaZona.map((h) => h.id));
      setCandSel(candidatosZona.map((h) => h.id));
      let total = 0;
      const cabe = (n: number) => (total += n) <= TOPE_TRANSCRIPCION;
      setInteracciones(interaccionesCuenta.filter((i) => cabe(i.caracteres)).map((i) => i.id));
      setSesiones(sesionesConTexto.filter((s) => cabe(s.caracteres)).map((s) => s.id));
      setContextos(CONTEXTOS_RONDA.map((x) => x.id));
    }
  }, [conv?.id, convId, zonaId, deLaZona, candidatosZona, sesionesConTexto, interaccionesCuenta]);

  const porDefecto = info?.instrucciones_default ?? "";
  const textoInstrucciones = instrucciones ?? porDefecto;
  const editadas = instrucciones !== null && instrucciones.trim() !== porDefecto.trim();
  const cambiarInstrucciones = (v: string | null) => { setInstrucciones(v); guardarInstrucciones(v); };
  const caracteres = sesionesConTexto.filter((s) => sesiones.includes(s.id)).reduce((n, s) => n + s.caracteres, 0)
    + interaccionesCuenta.filter((i) => interacciones.includes(i.id)).reduce((n, i) => n + i.caracteres, 0);
  const pasado = caracteres > TOPE_TRANSCRIPCION;
  const contexto: ContextoConversacion = {
    hallazgos_incluidos: elegidos, candidatos_incluidos: candSel, sesiones_incluidas: sesiones, interacciones_incluidas: interacciones, contextos,
    instrucciones: editadas ? textoInstrucciones : null,
  };

  // ── La caja de texto ──
  const inicial = zona
    ? (info?.mensaje_inicial_default ?? "").replace("{zona}", zona.nombre)
    : (info?.mensaje_inicial_general ?? "").replace("{empresa}", empresa);
  const [texto, setTexto] = useState("");
  const [tocado, setTocado] = useState(false);
  useEffect(() => { if (!convId && !tocado) setTexto(inicial); }, [convId, inicial, tocado]);
  const [vista, setVista] = useState<"historial" | "contexto">("contexto");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState<string | null>(null);
  const [ficha, setFicha] = useState<IdeaExplorada | null>(null);
  const [previa, setPrevia] = useState<VistaPreviaPrompt | null>(null);

  const mandar = useMandarMensaje(casoId);
  const verPrompt = useVistaPrevia(casoId);
  const listo = (t: string): boolean => {
    if (!t.trim()) return false;
    if (pasado) { setError("Las transcripciones e interacciones elegidas pasan del tope. Quita alguna en Contexto."); setVista("contexto"); return false; }
    if (!textoInstrucciones.trim()) { setError("Las instrucciones quedaron vacías. Restaura las de siempre."); setVista("contexto"); return false; }
    setError("");
    return true;
  };
  const cuerpo = (t: string) => ({ zona_caliente_id: zonaId, texto: t, contexto, esfuerzo, conversacion_id: convId });
  const enviar = async (t: string = texto) => {
    if (enviando || !listo(t)) return;
    const propio = t === texto;
    setEnviando(t.trim());
    if (propio) setTexto("");
    try {
      const c = await mandar.mutateAsync(cuerpo(t.trim()));
      if (!convId) setQuery({ conv: c.id });
      setTocado(false);
    } catch (e) {
      setError(mensaje(e));
      if (propio) setTexto(t);
    } finally { setEnviando(null); }
  };
  const mostrarPrompt = async () => {
    if (!listo(texto || "(mensaje vacío)")) return;
    try { setPrevia(await verPrompt.mutateAsync(cuerpo(texto.trim() || "(mensaje vacío)"))); }
    catch (e) { setError(mensaje(e)); }
  };
  const nueva = (z?: string | null) => { setQuery(z === null ? { zona: SIN_ZONA } : z ? { zona: z } : {}); setTocado(false); setError(""); setVista("contexto"); };
  const abrirConv = (id: string) => { setQuery({ conv: id }); setError(""); };

  const porId = useMemo(() => new Map([...(verificados ?? []), ...(candidatos ?? [])].map((h) => [h.id, h])), [verificados, candidatos]);
  const nombrePersona = useMemo(() => new Map((personas ?? []).map((p) => [p.id, p.nombre])), [personas]);
  const arquetipo = useMemo(() => new Map((info?.arquetipos ?? []).map((a) => [a.id, a.nombre])), [info]);

  // Bajar al último mensaje cuando llega algo nuevo.
  const fondo = useRef<HTMLDivElement>(null);
  useEffect(() => { fondo.current?.scrollIntoView({ block: "end" }); }, [conv?.mensajes.length, enviando]);

  if (!zonas || !verificados || !info) return <div className="tarjeta"><Esqueleto lineas={6} /></div>;

  const vacia = !convId;
  const n = (k: number, uno: string, varios: string) => (k ? ` · ${k} ${k === 1 ? uno : varios}` : "");
  const resumen = `${elegidos.length} verificado${elegidos.length === 1 ? "" : "s"}${n(candSel.length, "sin verificar", "sin verificar")}${n(interacciones.length, "interacción", "interacciones")}${n(sesiones.length, "transcripción", "transcripciones")}${editadas ? " · prompt editado" : ""}`;
  const caja = (
    <Composer texto={texto} onTexto={(v) => { setTexto(v); setTocado(true); }} onEnviar={() => void enviar()} ocupado={!!enviando}
      esfuerzo={esfuerzo} onEsfuerzo={setEsfuerzo} modelo={info.modelo} onVerPrompt={() => void mostrarPrompt()} armando={verPrompt.isPending}
      resumen={resumen} onResumen={() => setVista("contexto")} grande={vacia} />
  );
  const sujeto = zona ? `«${zona.nombre}»` : empresa;

  return (
    <div className="pg">
      {/* ── Panel izquierdo: historial y contexto ── */}
      <aside className="pg-aside">
        <div style={{ display: "flex", gap: 4, padding: 10, borderBottom: "1px solid var(--border)" }}>
          {([["historial", `Conversaciones${lista?.length ? ` · ${lista.length}` : ""}`], ["contexto", "Contexto"]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setVista(k)} className={"chip" + (vista === k ? " activo" : "")} style={{ flex: 1 }}>{l}</button>
          ))}
        </div>
        <div className="pg-scroll" style={{ padding: 12 }}>
          {vista === "historial" ? (
            <Historial lista={lista ?? []} activa={convId} nombreZona={nombreZona} onNueva={() => nueva()} onAbrir={abrirConv} />
          ) : (
            <PanelContexto
              conv={!!convId} zonaId={zonaId} activas={activas} onZona={(z) => nueva(z)} nombreZona={nombreZona}
              contextos={contextos} setContextos={setContextos} verificados={verificados} deLaZona={deLaZona} elegidos={elegidos} setElegidos={setElegidos}
              candidatos={candidatos ?? []} candSel={candSel} setCandSel={setCandSel}
              nombrePersona={nombrePersona} sesionesConTexto={sesionesConTexto} sesiones={sesiones} setSesiones={setSesiones}
              interaccionesCuenta={interaccionesCuenta} interacciones={interacciones} setInteracciones={setInteracciones}
              pasado={pasado} caracteres={caracteres} textoInstrucciones={textoInstrucciones} editadas={editadas} cambiarInstrucciones={cambiarInstrucciones}
            />
          )}
        </div>
      </aside>

      {/* ── La conversación ── */}
      <section className="pg-main">
        {!vacia && (
          <header style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--border)", minWidth: 0 }}>
            <Titulo casoId={casoId} id={convId!} titulo={conv?.titulo ?? "…"} />
            {conv && <span className="pastilla" style={{ fontSize: 11, background: "var(--sunken)", color: "var(--muted)", whiteSpace: "nowrap" }}>{zona?.nombre ?? "Toda la cuenta"}</span>}
            <button className="btn btn-secundario" style={{ marginLeft: "auto", whiteSpace: "nowrap" }} onClick={() => nueva(zonaId)}>+ Nueva</button>
          </header>
        )}
        <div className="pg-scroll">
          {vacia ? (
            <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "32px 24px", maxWidth: 760, margin: "0 auto", gap: 16 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 26, color: "var(--sky)", marginBottom: 6 }}>✦</div>
                <h2 className="titulo" style={{ fontSize: 22 }}>¿Qué exploramos de {sujeto}?</h2>
                <p style={{ color: "var(--muted)", fontSize: 13.5, marginTop: 6 }}>
                  La caja ya trae el mensaje de siempre. Edítalo o mándalo tal cual; después puedes pedir que explique, desglose o compare.
                  {verificados.length === 0 && " Todavía no hay hallazgos verificados: la IA trabaja con lo demás, y las ideas se vuelven ficha cuando algo verificado las sostenga."}
                </p>
              </div>
              {enviando ? <Pensando texto={enviando} esfuerzo={esfuerzo} /> : caja}
              {error && <p style={{ fontSize: 12.5, color: "var(--risk)", textAlign: "center" }}>{error}</p>}
            </div>
          ) : !conv ? (
            <div style={{ padding: 24 }}><Esqueleto lineas={6} /></div>
          ) : (
            <div style={{ maxWidth: 820, margin: "0 auto", padding: "20px 20px 8px", display: "flex", flexDirection: "column", gap: 18 }}>
              {conv.mensajes.map((m) => (
                <Mensaje key={m.id} m={m} porId={porId} arquetipo={arquetipo} casoId={casoId} ocupado={!!enviando}
                  onPedir={(t) => void enviar(t)} onFicha={setFicha} />
              ))}
              {enviando && <Pensando texto={enviando} esfuerzo={esfuerzo} />}
              <div ref={fondo} />
            </div>
          )}
        </div>
        {!vacia && (
          <div style={{ padding: "8px 16px 14px", maxWidth: 820, width: "100%", margin: "0 auto" }}>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8 }}>
              {SUGERENCIAS.map((s) => (
                <button key={s} className="chip" style={{ fontWeight: 400, flexShrink: 0 }} onClick={() => { setTexto(s); setTocado(true); }}>{s}</button>
              ))}
            </div>
            {caja}
            {error && <p style={{ fontSize: 12.5, color: "var(--risk)", marginTop: 6 }}>{error}</p>}
          </div>
        )}
      </section>

      {ficha && <ConvertirIdea casoId={casoId} cuentaId={cuentaId} zona={zona ?? null} activas={activas} verificados={verificados} idea={ficha} cerrar={() => setFicha(null)} />}
      {previa && <VerPrompt previa={previa} cerrar={() => setPrevia(null)} />}
    </div>
  );
}

// ─── Historial ──────────────────────────────────────────────────────────────

function Historial({ lista, activa, nombreZona, onNueva, onAbrir }: {
  lista: { id: string; titulo: string; zona_caliente_id: string | null; actualizada_en: string; ideas: number; guardadas: number; fichas: number }[];
  activa: string | null; nombreZona: Map<string, string>; onNueva: () => void; onAbrir: (id: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <button className="btn btn-primario" style={{ marginBottom: 8 }} onClick={onNueva}>+ Nueva conversación</button>
      {lista.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Todavía no hay conversaciones en este caso.</p>}
      {lista.map((c) => (
        <button key={c.id} className={"pg-conv" + (c.id === activa ? " activo" : "")} onClick={() => onAbrir(c.id)}>
          <span style={{ display: "block", fontSize: 13.5, fontWeight: c.id === activa ? 500 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.titulo}</span>
          <span style={{ display: "block", fontSize: 11.5, color: "var(--faint)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {c.zona_caliente_id ? nombreZona.get(c.zona_caliente_id) ?? "zona" : "Toda la cuenta"} · {fmtFecha(c.actualizada_en, true)}
            {c.ideas > 0 && ` · ${c.ideas} ideas`}{c.guardadas > 0 && ` · ${c.guardadas}★`}{c.fichas > 0 && ` · ${c.fichas} ficha${c.fichas === 1 ? "" : "s"}`}
          </span>
        </button>
      ))}
    </div>
  );
}

function Titulo({ casoId, id, titulo }: { casoId: string; id: string; titulo: string }) {
  const renombrar = useRenombrarConversacion(casoId);
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState(titulo);
  useEffect(() => setV(titulo), [titulo]);
  const guardar = async () => { setEditando(false); if (v.trim() && v.trim() !== titulo) await renombrar.mutateAsync({ id, titulo: v.trim() }).catch(() => setV(titulo)); };
  return editando ? (
    <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={() => void guardar()} style={{ fontSize: 14, fontWeight: 600, maxWidth: 360 }}
      onKeyDown={(e) => { if (e.key === "Enter") void guardar(); if (e.key === "Escape") { setV(titulo); setEditando(false); } }} />
  ) : (
    <button onClick={() => setEditando(true)} title="Renombrar" style={{ fontSize: 14, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titulo}</button>
  );
}

// ─── Contexto ───────────────────────────────────────────────────────────────

function PanelContexto(p: {
  conv: boolean; zonaId: string | null; activas: Zona[]; onZona: (z: string | null) => void; nombreZona: Map<string, string>;
  contextos: ContextoRonda[]; setContextos: (v: ContextoRonda[]) => void;
  verificados: Hallazgo[]; deLaZona: Hallazgo[]; elegidos: string[]; setElegidos: (v: string[]) => void; nombrePersona: Map<string, string>;
  candidatos: Hallazgo[]; candSel: string[]; setCandSel: (v: string[]) => void;
  sesionesConTexto: { id: string; tipo: string; fecha: string; palabras: number }[]; sesiones: string[]; setSesiones: (v: string[]) => void;
  interaccionesCuenta: { id: string; tipo: string; fecha: string; resumen: string; palabras: number }[]; interacciones: string[]; setInteracciones: (v: string[]) => void;
  pasado: boolean; caracteres: number; textoInstrucciones: string; editadas: boolean; cambiarInstrucciones: (v: string | null) => void;
}) {
  const alternar = <T,>(lista: T[], set: (v: T[]) => void, x: T) => set(lista.includes(x) ? lista.filter((y) => y !== x) : [...lista, x]);
  const otros = p.verificados.filter((h) => !p.deLaZona.includes(h));
  const grupo = (titulo: string, n: string, hijos: ReactNode, abierto = false) => (
    <details open={abierto} style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
      <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{titulo} <span style={{ color: "var(--faint)", fontWeight: 400 }}>· {n}</span></summary>
      <div style={{ marginTop: 8 }}>{hijos}</div>
    </details>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label style={{ fontSize: 12, color: "var(--muted)" }}>Sobre qué
        {p.conv ? (
          <span style={{ display: "block", fontSize: 13.5, color: "var(--text)", marginTop: 4 }}>{p.zonaId ? p.nombreZona.get(p.zonaId) : "Toda la cuenta"} <span style={{ color: "var(--faint)", fontSize: 11.5 }}>· para cambiarlo, conversación nueva</span></span>
        ) : (
          <select value={p.zonaId ?? SIN_ZONA} onChange={(e) => p.onZona(e.target.value === SIN_ZONA ? null : e.target.value)} style={{ marginTop: 4 }}>
            <option value={SIN_ZONA}>Toda la cuenta (sin zona)</option>
            {p.activas.map((z) => <option key={z.id} value={z.id}>Zona: {z.nombre}</option>)}
          </select>
        )}
      </label>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {CONTEXTOS_RONDA.map((x) => (
          <label key={x.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, cursor: "pointer", lineHeight: 1.35 }}>
            <input type="checkbox" style={{ width: "auto", marginTop: 2 }} checked={p.contextos.includes(x.id)} onChange={() => alternar(p.contextos, p.setContextos, x.id)} />
            <span>{x.label}<span style={{ display: "block", fontSize: 11.5, color: "var(--faint)" }}>{x.pista}</span></span>
          </label>
        ))}
      </div>

      <div>
        {grupo("Hallazgos verificados", `${p.elegidos.length} de ${p.verificados.length}`, <>
          {p.verificados.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)", marginBottom: 6 }}>Todavía no hay verificados. Se puede conversar igual; para volver una idea ficha hace falta anclarla a algo verificado.</p>}
          {p.verificados.length > 0 && p.deLaZona.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)", marginBottom: 6 }}>Esta zona no tiene hallazgos asignados. Elige abajo los que la expliquen.</p>}
          <ListaHallazgos hallazgos={p.deLaZona} elegidos={p.elegidos} onChange={p.setElegidos} nombre={p.nombrePersona} />
          {otros.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--sky)" }}>Otros del caso · {otros.length}</summary>
              <div style={{ marginTop: 6 }}><ListaHallazgos hallazgos={otros} elegidos={p.elegidos} onChange={p.setElegidos} nombre={p.nombrePersona} /></div>
            </details>
          )}
        </>, true)}
        {grupo("Hallazgos sin verificar", `${p.candSel.length} de ${p.candidatos.length}`, <>
          {p.candidatos.length === 0
            ? <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Ninguno en la cola de verificación.</p>
            : <p style={{ fontSize: 11.5, color: "var(--faint)", marginBottom: 6 }}>La IA los lee como contexto, pero no sostienen ninguna ficha.</p>}
          <ListaHallazgos hallazgos={p.candidatos} elegidos={p.candSel} onChange={p.setCandSel} nombre={p.nombrePersona} />
        </>)}
        {grupo("Transcripciones de sesiones", `${p.sesiones.length} de ${p.sesionesConTexto.length}`, <>
          {p.sesionesConTexto.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Ninguna sesión tiene transcripción. Se adjunta al registrar la sesión.</p>}
          {p.sesionesConTexto.map((s) => (
            <label key={s.id} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 13, padding: "3px 0", cursor: "pointer" }}>
              <input type="checkbox" style={{ width: "auto" }} checked={p.sesiones.includes(s.id)} onChange={() => alternar(p.sesiones, p.setSesiones, s.id)} />
              <span style={{ flex: 1 }}>{SESIONES.find((x) => x.id === s.tipo)?.nombre ?? s.tipo} <span style={{ color: "var(--faint)" }}>· {fmtFecha(s.fecha, true)}</span></span>
              <span className="mono" style={{ color: "var(--faint)", fontSize: 11.5 }}>{s.palabras.toLocaleString("es-MX")} pal.</span>
            </label>
          ))}
        </>)}
        {grupo("Interacciones de la cuenta", `${p.interacciones.length} de ${p.interaccionesCuenta.length}`, <>
          {p.interaccionesCuenta.length === 0 && <p style={{ fontSize: 12.5, color: "var(--faint)" }}>Sin interacciones registradas.</p>}
          {p.interaccionesCuenta.map((i) => (
            <label key={i.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, padding: "3px 0", cursor: "pointer", lineHeight: 1.4 }}>
              <input type="checkbox" style={{ width: "auto", marginTop: 3 }} checked={p.interacciones.includes(i.id)} onChange={() => alternar(p.interacciones, p.setInteracciones, i.id)} />
              <span style={{ flex: 1 }}>
                <span className="tag11" style={{ color: "var(--faint)", marginRight: 6 }}>{TIPOS_INTERACCION[i.tipo as keyof typeof TIPOS_INTERACCION] ?? i.tipo} · {fmtFecha(i.fecha)}</span>{i.resumen}
                {i.palabras > 0 && <span style={{ color: "var(--faint)", fontSize: 11.5 }}> · transcripción {i.palabras.toLocaleString("es-MX")} pal.</span>}
              </span>
            </label>
          ))}
        </>)}
        {grupo("Instrucciones (prompt)", p.editadas ? "editadas" : "las de siempre", <>
          <textarea value={p.textoInstrucciones} onChange={(e) => p.cambiarInstrucciones(e.target.value)} rows={14} spellCheck={false}
            style={{ fontSize: 12, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }} />
          <button type="button" className="btn btn-secundario" style={{ marginTop: 6 }} disabled={!p.editadas} onClick={() => p.cambiarInstrucciones(null)}>Restaurar las de siempre</button>
          <p style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 6 }}>Se recuerdan en este navegador. Aunque las cambies, solo cuentan anclas verificadas y la jerga se marca igual.</p>
        </>)}
      </div>
      {p.pasado && <p style={{ fontSize: 12, color: "var(--risk)" }}>Las transcripciones pasan del tope ({p.caracteres.toLocaleString("es-MX")} de {TOPE_TRANSCRIPCION.toLocaleString("es-MX")} caracteres). Quita alguna.</p>}
    </div>
  );
}

function ListaHallazgos({ hallazgos, elegidos, onChange, nombre }: { hallazgos: Hallazgo[]; elegidos: string[]; onChange: (v: string[]) => void; nombre: Map<string, string> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {hallazgos.map((h) => (
        <label key={h.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" }}>
          <input type="checkbox" checked={elegidos.includes(h.id)} style={{ width: "auto", marginTop: 3 }}
            onChange={() => onChange(elegidos.includes(h.id) ? elegidos.filter((x) => x !== h.id) : [...elegidos, h.id])} />
          <span>
            <span className="tag11" style={{ color: h.tipo === "ansiedad" ? "var(--conflict)" : "var(--faint)", marginRight: 6 }}>{TIPOS_HALLAZGO[h.tipo]}</span>
            {h.contenido}
            <span style={{ color: "var(--faint)", fontSize: 11.5 }}> · {h.visibilidad === "anonimizada" ? "anónimo" : nombre.get(h.persona_id) ?? h.persona_id}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

// ─── La caja de texto ───────────────────────────────────────────────────────

function Composer({ texto, onTexto, onEnviar, ocupado, esfuerzo, onEsfuerzo, modelo, onVerPrompt, armando, resumen, onResumen, grande }: {
  texto: string; onTexto: (v: string) => void; onEnviar: () => void; ocupado: boolean; esfuerzo: Esfuerzo; onEsfuerzo: (v: Esfuerzo) => void; modelo: string;
  onVerPrompt: () => void; armando: boolean; resumen: string; onResumen: () => void; grande: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const t = ref.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${Math.min(t.scrollHeight, grande ? 260 : 200)}px`;
  }, [texto, grande]);
  const tecla = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onEnviar(); }
  };
  return (
    <div className="pg-composer">
      <textarea ref={ref} value={texto} onChange={(e) => onTexto(e.target.value)} onKeyDown={tecla} rows={grande ? 3 : 1}
        placeholder="Pide ideas, que explique una, que desglose, que compare…" aria-label="Mensaje" />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <button type="button" onClick={onResumen} style={{ fontSize: 11.5, color: "var(--muted)", textAlign: "left" }} title="Cambiar el contexto">📎 {resumen}</button>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--muted)" }} title={ESFUERZOS.find((x) => x.id === esfuerzo)?.pista}>
          <span style={{ whiteSpace: "nowrap" }}>{nombreModelo(modelo)} ·</span>
          <select aria-label="Nivel de razonamiento" value={esfuerzo} onChange={(e) => onEsfuerzo(e.target.value as Esfuerzo)}
            style={{ width: "auto", fontSize: 11.5, padding: "2px 4px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--card)", color: esfuerzo === "medium" ? "var(--muted)" : "var(--sky)" }}>
            {ESFUERZOS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <button type="button" onClick={onVerPrompt} disabled={armando} style={{ fontSize: 11.5, color: "var(--muted)" }}>{armando ? "Armando…" : "Ver lo que se manda"}</button>
        <button type="button" className="btn btn-primario" style={{ marginLeft: "auto", borderRadius: 999, padding: "6px 14px" }} disabled={ocupado || !texto.trim()} onClick={onEnviar} aria-label="Enviar">
          {ocupado ? "…" : "Enviar ↑"}
        </button>
      </div>
    </div>
  );
}

// ─── Mensajes ───────────────────────────────────────────────────────────────

const ESPERA: Record<Esfuerzo, string> = {
  low: "unos segundos", medium: "entre 30 segundos y un minuto y medio", high: "un par de minutos",
  xhigh: "varios minutos", max: "varios minutos; es el nivel más lento",
};

/** "claude-opus-5-5" → "Opus 5.5". */
function nombreModelo(id: string): string {
  const m = /^claude-([a-z]+)-(\d+)(?:-(\d+))?/.exec(id);
  return m ? `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}${m[3] ? `.${m[3]}` : ""}` : id;
}

function Pensando({ texto, esfuerzo }: { texto: string; esfuerzo: Esfuerzo }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Burbuja texto={texto} />
      <div style={{ display: "flex", gap: 10 }} aria-live="polite">
        <span style={{ color: "var(--sky)", fontSize: 18 }}>✦</span>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>Pensando… suele tardar {ESPERA[esfuerzo]}.</p>
          <Esqueleto lineas={3} />
        </div>
      </div>
    </div>
  );
}

function Burbuja({ texto, autor }: { texto: string; autor?: string | null }) {
  return (
    <div style={{ alignSelf: "flex-end", maxWidth: "85%", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
      {autor && <span style={{ fontSize: 11, color: "var(--faint)" }}>{nombreEquipo(autor)}</span>}
      <div style={{ background: "var(--sunken)", borderRadius: 14, padding: "8px 14px", fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{texto}</div>
    </div>
  );
}

function Mensaje({ m, porId, arquetipo, casoId, ocupado, onPedir, onFicha }: {
  m: MensajeExploracion; porId: Map<string, Hallazgo>; arquetipo: Map<string, string>; casoId: string; ocupado: boolean;
  onPedir: (t: string) => void; onFicha: (i: IdeaExplorada) => void;
}) {
  if (m.rol === "usuario") return <Burbuja texto={m.texto} autor={m.autor} />;
  return (
    <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
      <span style={{ color: "var(--sky)", fontSize: 18, lineHeight: 1.2 }}>✦</span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {m.texto && <Md texto={m.texto} />}
        {m.ideas.map((i) => <TarjetaIdea key={i.id} i={i} porId={porId} arquetipo={arquetipo} casoId={casoId} ocupado={ocupado} onPedir={onPedir} onFicha={onFicha} />)}
      </div>
    </div>
  );
}

/** Markdown ligero (párrafos, viñetas, listas numeradas, **negritas**, *cursivas*, `código`), sin HTML crudo. */
function Md({ texto }: { texto: string }) {
  const enLinea = (s: string): ReactNode[] =>
    s.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g).filter(Boolean).map((t, i) =>
      t.startsWith("**") && t.endsWith("**") ? <strong key={i}>{t.slice(2, -2)}</strong>
        : t.startsWith("`") && t.endsWith("`") ? <code key={i}>{t.slice(1, -1)}</code>
        : t.startsWith("*") && t.endsWith("*") && t.length > 2 ? <em key={i}>{t.slice(1, -1)}</em>
        : <Fragment key={i}>{t}</Fragment>);
  const bloques = texto.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return (
    <div className="pg-md" style={{ fontSize: 14, lineHeight: 1.6, overflowWrap: "anywhere" }}>
      {bloques.map((b, i) => {
        const lineas = b.split("\n").filter((l) => l.trim());
        if (lineas.length && lineas.every((l) => /^\s*[-*•]\s+/.test(l))) return <ul key={i}>{lineas.map((l, j) => <li key={j}>{enLinea(l.replace(/^\s*[-*•]\s+/, ""))}</li>)}</ul>;
        if (lineas.length && lineas.every((l) => /^\s*\d+[.)]\s+/.test(l))) return <ol key={i}>{lineas.map((l, j) => <li key={j}>{enLinea(l.replace(/^\s*\d+[.)]\s+/, ""))}</li>)}</ol>;
        if (/^#{1,4}\s/.test(b)) return <p key={i}><strong>{enLinea(b.replace(/^#{1,4}\s+/, ""))}</strong></p>;
        return <p key={i}>{lineas.map((l, j) => <Fragment key={j}>{j > 0 && <br />}{enLinea(l)}</Fragment>)}</p>;
      })}
    </div>
  );
}

function TarjetaIdea({ i, porId, arquetipo, casoId, ocupado, onPedir, onFicha }: {
  i: IdeaExplorada; porId: Map<string, Hallazgo>; arquetipo: Map<string, string>; casoId: string; ocupado: boolean;
  onPedir: (t: string) => void; onFicha: (i: IdeaExplorada) => void;
}) {
  const marcar = useMarcarIdea(casoId);
  const [descartando, setDescartando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState("");
  const sinAncla = i.avisos.includes("sin_ancla");
  const jerga = i.avisos.filter((a) => a.startsWith("jerga:")).map((a) => a.slice(6));
  const cambiar = async (estado: "nueva" | "guardada" | "descartada", m?: string) => {
    setError("");
    try { await marcar.mutateAsync({ ideaId: i.id, estado, motivo: m }); setDescartando(false); setMotivo(""); } catch (e) { setError(mensaje(e)); }
  };
  const apagada = i.estado === "descartada";
  const pedidos: [string, string][] = [
    ["Desglosar", `Desglosa la idea «${i.titulo}»: qué se construye, qué datos necesitamos, qué riesgos hay con la gente y cómo la mediríamos.`],
    ["Más como esta", `Propón 3 ideas como «${i.titulo}», atacando lo mismo de otra forma.`],
    ["Más simple", `Propón 2 versiones más simples de «${i.titulo}»: menos piezas, algo que se pruebe en dos semanas.`],
  ];

  return (
    <article style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "12px 14px", background: i.estado === "guardada" ? "var(--ice)" : "var(--card)", opacity: apagada ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 600, flex: 1, minWidth: 180, textDecoration: apagada ? "line-through" : "none" }}>{i.estado === "guardada" && "★ "}{i.titulo}</h3>
        <span className="tag11" style={{ color: i.arquetipo_id ? "var(--muted)" : "var(--sky)" }}>{i.arquetipo_id ? arquetipo.get(i.arquetipo_id) ?? i.arquetipo_id : "idea nueva"}</span>
      </div>
      <p style={{ fontSize: 13.5, lineHeight: 1.5, marginTop: 4 }}>{i.una_linea}</p>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--muted)", marginTop: 4 }}>{i.como_funciona}</p>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 10px", fontSize: 12.5, marginTop: 8, lineHeight: 1.45 }}>
        {i.primer_paso_30d && <><dt style={{ color: "var(--faint)" }}>30 días</dt><dd>{i.primer_paso_30d}</dd></>}
        {i.dato_baseline && <><dt style={{ color: "var(--faint)" }}>Baseline</dt><dd>{i.dato_baseline}</dd></>}
        {i.toca_ansiedades && <><dt style={{ color: "var(--conflict)" }}>Personas</dt><dd>{i.toca_ansiedades}</dd></>}
        <dt style={{ color: "var(--faint)" }}>Se apoya en</dt>
        <dd style={{ display: "flex", gap: 4, flexWrap: "wrap", minWidth: 0 }}>
          {i.hallazgos_ancla.map((h) => <span key={h} className="pastilla" title={porId.get(h)?.contenido} style={{ fontSize: 11, background: "var(--sunken)", color: "var(--text)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{porId.get(h)?.contenido ?? h}</span>)}
          {sinAncla && <Pastilla estado="risk">sin ancla verificada</Pastilla>}
        </dd>
      </dl>
      {jerga.length > 0 && <p style={{ fontSize: 12, color: "var(--proposed)", marginTop: 6 }}>Jerga a reescribir antes del Taller: {jerga.join(", ")}</p>}
      {apagada && i.motivo_descarte && <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6 }}>Descartada: {i.motivo_descarte}</p>}

      {i.estado === "convertida" ? (
        <p style={{ fontSize: 12.5, color: "var(--verified)", marginTop: 10 }}>✓ Ya es ficha de la cartera</p>
      ) : descartando ? (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Por qué no: la IA lo toma en cuenta" style={{ flex: "1 1 220px" }}
            onKeyDown={(e) => { if (e.key === "Enter" && motivo.trim()) { e.preventDefault(); void cambiar("descartada", motivo.trim()); } if (e.key === "Escape") setDescartando(false); }} />
          <button className="btn btn-peligro" disabled={!motivo.trim() || marcar.isPending} onClick={() => void cambiar("descartada", motivo.trim())}>Descartar</button>
          <button className="btn btn-secundario" onClick={() => setDescartando(false)}>Cancelar</button>
        </div>
      ) : apagada ? (
        <div style={{ marginTop: 10 }}><button className="btn btn-secundario" disabled={marcar.isPending} onClick={() => void cambiar("nueva")}>Recuperar</button></div>
      ) : (
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn btn-secundario" disabled={marcar.isPending} onClick={() => void cambiar(i.estado === "guardada" ? "nueva" : "guardada")}>{i.estado === "guardada" ? "Quitar ★" : "★ Guardar"}</button>
          {pedidos.map(([l, t]) => <button key={l} className="btn btn-secundario" disabled={ocupado} onClick={() => onPedir(t)}>{l}</button>)}
          <button className="btn btn-secundario" style={{ color: "var(--risk)" }} onClick={() => setDescartando(true)}>✕</button>
          <button className="btn btn-primario" style={{ marginLeft: "auto" }} disabled={sinAncla} title={sinAncla ? "Sin ancla verificada no puede ser ficha" : undefined} onClick={() => onFicha(i)}>→ Ficha</button>
        </div>
      )}
      {error && <p style={{ fontSize: 12.5, color: "var(--risk)", marginTop: 6 }}>{error}</p>}
    </article>
  );
}

// ─── Modales ────────────────────────────────────────────────────────────────

function VerPrompt({ previa, cerrar }: { previa: VistaPreviaPrompt; cerrar: () => void }) {
  const bloque = { whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12, lineHeight: 1.5, background: "var(--sunken)", borderRadius: 8, padding: "10px 12px", maxHeight: 280, overflow: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } as const;
  return (
    <Modal titulo="Lo que se le mandaría a la IA" sub={`${previa.modelo} · esfuerzo ${previa.effort} · ~${Math.round(previa.caracteres / 4).toLocaleString("es-MX")} tokens de entrada`} onCerrar={cerrar} ancho={860}
      pie={<button className="btn btn-primario" onClick={cerrar}>Cerrar</button>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div><span className="tag11" style={{ color: "var(--faint)", display: "block", marginBottom: 4 }}>Instrucciones (sistema)</span><pre style={bloque}>{previa.sistema}</pre></div>
        {previa.mensajes.map((m, i) => (
          <div key={i}><span className="tag11" style={{ color: "var(--faint)", display: "block", marginBottom: 4 }}>{m.role === "user" ? (i === previa.mensajes.length - 1 ? "Tu mensaje nuevo" : "Equipo") : "IA"}</span><pre style={bloque}>{m.content}</pre></div>
        ))}
      </div>
    </Modal>
  );
}

/** La ficha cuelga de la zona de la conversación; si fue sobre toda la cuenta, se elige aquí. */
function ConvertirIdea({ casoId, cuentaId, zona, activas, verificados, idea, cerrar }: {
  casoId: string; cuentaId: string; zona: Zona | null; activas: Zona[]; verificados: Hallazgo[]; idea: IdeaExplorada; cerrar: () => void;
}) {
  const convertir = useConvertirIdea(casoId, cuentaId);
  if (!zona && activas.length === 0) {
    return (
      <Modal titulo="Falta una zona caliente" onCerrar={cerrar} pie={<button className="btn btn-primario" onClick={cerrar}>Entendido</button>}>
        <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>Toda ficha de la cartera cuelga de una zona caliente. Marca una en la pestaña Descubrimiento (sobre un hallazgo verificado) y vuelve a convertir esta idea; la conversación se queda guardada.</p>
      </Modal>
    );
  }
  return (
    <FormOportunidad
      ctx={{ casoId, cuentaId, verificados }} zonas={zona ? [zona] : activas} zonaFija={!!zona} cerrar={cerrar}
      sub="Revisa el título y la línea en palabras del dueño. Entra a la cartera como cualquier ficha, lista para la Matriz."
      inicial={{ titulo: idea.titulo, linea: idea.una_linea, zona: zona?.id ?? activas[0].id, anclas: idea.hallazgos_ancla }}
      guardar={(v) => convertir.mutateAsync({ ideaId: idea.id, oportunidad_id: idNuevo("o"), titulo: v.titulo, descripcion_una_linea: v.linea, hallazgos_ancla: v.anclas, zona_caliente_id: v.zona })}
    />
  );
}
